import { CreatePostDto, PostDto, PostListDto, PostListQuery, UpdatePostDto } from "@growth/dto";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

export const postKeys = {
  all: ["posts"] as const,
  list: (query: Partial<PostListQuery>) => ["posts", "list", query] as const,
  detail: (id: string) => ["posts", "detail", id] as const,
};

/** Posts on the active brand's blog (the X-Brand-Id header decides which). */
export const usePostList = (query: Partial<PostListQuery>) =>
  useQuery({
    queryKey: postKeys.list(query),
    queryFn: async () => (await api.get<PostListDto>("/posts", { params: query })).data,
    placeholderData: keepPreviousData,
  });

export const usePost = (id: string) =>
  useQuery({
    queryKey: postKeys.detail(id),
    queryFn: async () => (await api.get<PostDto>(`/posts/${id}`)).data,
    // The editor owns the working copy; don't clobber it with background refetches.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

export const useCreatePost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePostDto) => (await api.post<PostDto>("/posts", input)).data,
    onSuccess: (post) => {
      queryClient.setQueryData(postKeys.detail(post.id), post);
      void queryClient.invalidateQueries({ queryKey: ["posts", "list"] });
    },
  });
};

export const updatePost = async (id: string, changes: UpdatePostDto) =>
  (await api.patch<PostDto>(`/posts/${id}`, changes)).data;

type PostAction = "publish" | "unpublish";

export const usePostAction = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: PostAction) =>
      (await api.post<PostDto>(`/posts/${id}/${action}`)).data,
    onSuccess: (post) => {
      queryClient.setQueryData(postKeys.detail(id), post);
      void queryClient.invalidateQueries({ queryKey: ["posts", "list"] });
    },
  });
};

export const useDeletePost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/posts/${id}`);
      return id;
    },
    onSuccess: (id) => {
      queryClient.removeQueries({ queryKey: postKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: ["posts", "list"] });
    },
  });
};
